import "./Controls.scss";

import { useEffect, useMemo } from "react";
import { Accordion, Form, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";

import { StyledSelect } from "../../../../components/StyledSelect/StyledSelect";
import { Switch } from "../../../../components/Switch/Switch";
import { useApplications } from "../../../../hooks/reactQueryHooks/useApplications";
import { useKeys } from "../../../../hooks/reactQueryHooks/useKeys";
import { usePlaygroundModels } from "../../../../hooks/reactQueryHooks/usePlayground";
import { MAX_CHOICES } from "../../../../hooks/reactQueryHooks/useProxy";
import { cacheTypes } from "../../constants";
import type {
  CacheType,
  IModelOptionType,
  MessageType,
  OptionType,
} from "../../types";
import { ResponseFormat, type ResponseFormatProps } from "../ResponseFormat";
import { Tools, type ToolsProps } from "../Tools";

interface ControlsProps {
  organizationId: string;
  selectedModel: IModelOptionType | null;
  apiKey: OptionType | null;
  cacheType: CacheType;
  systemMessage: MessageType;
  seed: number | null;
  stream: boolean;
  n: number;
  setCacheType: (cacheType: CacheType) => void;
  setSelectedModel: (model: IModelOptionType) => void;
  setApiKey: (apiKey: OptionType) => void;
  editMessage: (message: MessageType) => void;
  setSeed: (seed: number) => void;
  setStream: (stream: boolean) => void;
  setN: (n: number) => void;
}

const Controls: React.FC<ControlsProps & ToolsProps & ResponseFormatProps> = ({
  organizationId,
  selectedModel,
  apiKey,
  cacheType,
  systemMessage,
  seed,
  stream,
  selectedTools,
  n,
  responseFormat,
  setCacheType,
  setSelectedModel,
  setApiKey,
  editMessage,
  setSeed,
  setStream,
  setSelectedTools,
  setN,
  setResponseFormat,
}) => {
  const { data: keysList, isLoading: isKeysLoading } = useKeys({
    organizationId,
    type: "proxy",
    pageSize: 100,
  });

  const { data: applicationsList, isLoading: isAppsLoading } =
    useApplications(organizationId);

  const { data: modelsData, isLoading: isModelsLoading } =
    usePlaygroundModels();

  const keysOptions = useMemo(() => {
    if (!keysList?.items || !applicationsList) return [];
    return keysList.items
      .map(key => ({
        value: key.key || "",
        label: `${key.name} (${
          applicationsList?.find(
            application => application.id === key.applicationId,
          )?.name
        })`,
      }))
      .filter(key => !key.label.includes("undefined"));
  }, [keysList, applicationsList]);

  const modelOptions = useMemo(() => {
    if (!modelsData) return [];
    return modelsData.map(model => ({
      value: model.model,
      label: model.displayName,
      withTools: model.isToolsSupportEnabled,
    }));
  }, [modelsData]);

  const editSystemMEssage = (e: React.ChangeEvent<HTMLInputElement>) => {
    editMessage({
      ...systemMessage,
      choices: [
        {
          ...systemMessage.choices[0],
          content: e.target.value,
        },
      ],
    });
  };

  useEffect(() => {
    if (keysOptions.length > 0 && !apiKey) {
      setApiKey(keysOptions[0]);
    }
  }, [keysOptions, applicationsList]);

  useEffect(() => {
    if (modelOptions.length > 0 && !selectedModel) {
      setSelectedModel(modelOptions[0]);
    }
  }, [modelOptions]);

  return (
    <div className="card playground-controls">
      <div className="app-container-inner-wrapper d-flex flex-column ">
        <div className="playground-controls-item">
          <label>Model</label>
          {isModelsLoading || (!selectedModel && modelsData) ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner size="sm" animation="border" />
            </div>
          ) : (
            <StyledSelect
              options={modelOptions}
              value={selectedModel || undefined}
              onChange={setSelectedModel}
              placeholder="Select a model"
            />
          )}
        </div>
        <div className="playground-controls-item">
          <label>Project API Key</label>
          {isKeysLoading ||
          isAppsLoading ||
          (!apiKey && isAppsLoading && isKeysLoading) ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner size="sm" animation="border" />
            </div>
          ) : !keysOptions.length ? (
            <div className="d-flex align-items-center gap-2">
              <p className="my-0 text-danger">No API keys found.</p>
              <Link to="/organization/keys" className="btn btn-primary">
                Generate API key
              </Link>
            </div>
          ) : (
            <StyledSelect
              options={keysOptions}
              value={apiKey || undefined}
              onChange={setApiKey}
              placeholder="Select API key"
            />
          )}
        </div>
        <div className="playground-controls-item">
          <label>Cache-Control</label>
          {cacheTypes.map((type, index) => (
            <div
              key={index}
              className="d-flex align-items-start playground-cache-type"
            >
              <Form.Check
                type="radio"
                id={type.title}
                checked={type.title === cacheType.title}
                onChange={() => setCacheType(type)}
              />
              <label htmlFor={type.title} className="form-check-label px-2">
                {type.title}
                <p>{type.description}</p>
              </label>
            </div>
          ))}
        </div>

        {selectedModel?.withTools && (
          <Tools
            selectedTools={selectedTools}
            setSelectedTools={setSelectedTools}
          />
        )}

        <ResponseFormat
          responseFormat={responseFormat}
          setResponseFormat={setResponseFormat}
        />

        <Accordion className="playground-accordion">
          <Accordion.Item eventKey="0">
            <Accordion.Header>Advanced</Accordion.Header>
            <Accordion.Body>
              <div className="playground-controls-item">
                <label>System instructions</label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="Enter system instructions here..."
                  value={systemMessage.choices[0]?.content || ""}
                  onChange={editSystemMEssage}
                />
              </div>
              <div className="playground-controls-item">
                <Switch
                  label="Stream"
                  onChange={() => {
                    setStream(!stream);
                  }}
                  checked={stream}
                />
              </div>
              <div className="playground-controls-item">
                <label htmlFor="playground-seed">Seed</label>
                <Form.Control
                  id="playground-seed"
                  placeholder="Enter seed  here..."
                  value={seed || ""}
                  onChange={e => setSeed(Number(e.target.value))}
                  type="number"
                />
              </div>
              <div className="playground-controls-item">
                <label htmlFor="playground-seed">
                  Number of responses (N parameter)
                </label>
                <div className="d-flex justify-content-space-between">
                  <Form.Range
                    min={1}
                    max={MAX_CHOICES}
                    step={1}
                    value={n}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setN(Number(e.target.value))
                    }
                    id="similarity-threshold"
                  />
                  <span className="range-value">{n}</span>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
    </div>
  );
};

export { Controls };
